import React, { useEffect, useState } from "react";
import { Table, Button, Space, Typography, Popconfirm, message, Tooltip, Tag, Select, Dropdown, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { fetchApprovalTemplates, deleteApprovalTemplate, updateTemplateStatus } from "../../api";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ActionType, ModuleType, PermissionButton } from "../../../../components";
import { EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import "bootstrap/dist/css/bootstrap.min.css";

const { Title, Text } = Typography;

const STATUS_CONFIG = {
  ACTIVE:   { color: "success", label: "Active" },
  INACTIVE: { color: "warning", label: "Inactive" },
  DRAFT:    { color: "default", label: "Draft" },
};

const STATUS_TRANSITIONS = {
  DRAFT:    [{ label: "Activate", next: "ACTIVE" }, { label: "Deactivate", next: "INACTIVE" }],
  ACTIVE:   [{ label: "Set Draft", next: "DRAFT" }, { label: "Deactivate", next: "INACTIVE" }],
  INACTIVE: [{ label: "Activate", next: "ACTIVE" }, { label: "Set Draft", next: "DRAFT" }],
};

const ApprovalTemplateList = () => {
  const { canViewModule, canViewField, canEditField, can } = usePermissions();
  const module = ModuleType.APPROVALS;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const fetchTemplatesData = async () => {
    setLoading(true);
    try {
      const templateData = await fetchApprovalTemplates();
      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch {
      setTemplates([]);
      messageApi.error("Failed to load templates");
    }
    setLoading(false);
  };

  useEffect(() => { fetchTemplatesData(); }, []);

  const handleDelete = async (id) => {
    try {
      await deleteApprovalTemplate(id);
      messageApi.success("Template deleted");
      setTemplates((prev) => prev.filter((tpl) => tpl._id !== id));
    } catch {
      messageApi.error("Failed to delete template");
    }
  };

  const handleStatusChange = async (id, nextStatus) => {
    try {
      const updated = await updateTemplateStatus(id, nextStatus);
      setTemplates((prev) => prev.map((tpl) => tpl._id === id ? { ...tpl, status: updated.status, isActive: updated.isActive } : tpl));
      messageApi.success(`Template set to ${STATUS_CONFIG[nextStatus]?.label}`);
    } catch {
      messageApi.error("Failed to update status");
    }
  };

  if (!canViewModule(module)) {
    return (
      <div className="container-fluid px-2 px-md-4 py-3">
        <div className="text-center mt-5">
          <Text type="danger">You do not have permission to view approval templates.</Text>
        </div>
      </div>
    );
  }

  const displayedTemplates = statusFilter
    ? templates.filter((t) => (t.status || (t.isActive ? 'ACTIVE' : 'INACTIVE')) === statusFilter)
    : templates;

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      responsive: ["md"],
      render: (text) => canViewField(module, "module") ? text : "***",
    },
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
      render: (text) => canViewField(module, "name") ? text : "***",
    },
    {
      title: "Levels",
      dataIndex: "steps",
      key: "levels",
      render: (steps) => canViewField(module, "levels") ? (steps?.length || 0) : "***",
      responsive: ["sm"],
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => {
        const s = record.status || (record.isActive ? "ACTIVE" : "INACTIVE");
        const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.DRAFT;
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Rule",
      dataIndex: "rules",
      key: "rule",
      render: (rules) => {
        if (!canViewField(module, "rules")) return "***";
        if (!rules || !Object.keys(rules).length) return "-";
        const ruleStr = JSON.stringify(rules);
        return (
          <Tooltip title={<pre style={{ maxWidth: 400, whiteSpace: "pre-wrap" }}>{JSON.stringify(rules, null, 2)}</pre>}>
            <span style={{ fontSize: 12, fontFamily: "monospace", cursor: "pointer" }}>
              {ruleStr.length > 40 ? ruleStr.slice(0, 40) + "..." : ruleStr}
            </span>
          </Tooltip>
        );
      },
      responsive: ["md"],
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => canViewField(module, "createdAt") ? (v ? new Date(v).toLocaleString() : "-") : "***",
      responsive: ["lg"],
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => {
        const currentStatus = record.status || (record.isActive ? "ACTIVE" : "INACTIVE");
        const transitions = STATUS_TRANSITIONS[currentStatus] || [];
        const canEdit = canEditField(module, "details");
        const hasEditPerm = can(module, ActionType.EDIT);
        const hasDeletePerm = can(module, ActionType.DELETE);

        const actionItems = [];

        // Edit option
        actionItems.push({
          key: "edit",
          label: "Edit",
          icon: <EditOutlined />,
          disabled: !hasEditPerm,
          onClick: () => navigate(`/approvals/approval-templates/${record._id}/edit`),
        });

        // Transitions options
        if (canEdit && transitions.length > 0) {
          transitions.forEach(({ label, next }) => {
            actionItems.push({
              key: `status-${next}`,
              label: label,
              onClick: () => {
                Modal.confirm({
                  title: `Set template to "${STATUS_CONFIG[next]?.label}"?`,
                  content: `Are you sure you want to change the status of this template?`,
                  okText: "Yes",
                  cancelText: "No",
                  onOk: () => handleStatusChange(record._id, next),
                });
              },
            });
          });
        }

        // Delete option
        actionItems.push({
          key: "delete",
          label: "Delete",
          icon: <DeleteOutlined />,
          danger: true,
          disabled: !hasDeletePerm,
          onClick: () => {
            Modal.confirm({
              title: "Delete this template?",
              content: "Are you sure you want to delete this template? This action cannot be undone.",
              okText: "Yes",
              okType: "danger",
              cancelText: "No",
              onOk: () => handleDelete(record._id),
            });
          },
        });

        return (
          <Dropdown menu={{ items: actionItems }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="container-fluid px-2 px-md-4 py-3">
      {contextHolder}
      <div className="row align-items-center mb-3">
        <div className="col-12 col-md-6">
          <Title level={3} style={{ margin: 0, fontSize: "1.3rem" }}>
            Approval Rules
          </Title>
        </div>
        <div className="col-12 col-md-6 d-flex gap-2 justify-content-md-end mt-2 mt-md-0 flex-wrap">
          <Select
            allowClear
            placeholder="Filter by status"
            style={{ minWidth: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: "Active", value: "ACTIVE" },
              { label: "Inactive", value: "INACTIVE" },
              { label: "Draft", value: "DRAFT" },
            ]}
          />
          <PermissionButton
            buttonType={ActionType.CREATE}
            module={module}
            type="primary"
            style={{ minWidth: 120 }}
            onClick={() => navigate("/approvals/approval-templates/create")}
          >
            Create Rule
          </PermissionButton>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div style={{ overflowX: "auto" }}>
            <Table
              columns={columns}
              dataSource={displayedTemplates}
              loading={loading}
              rowKey="_id"
              pagination={{ pageSize: 10, showSizeChanger: false, responsive: true }}
              size="middle"
              scroll={{ x: 600 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalTemplateList;
